/**
 * Logins hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { loginsApi } from '../api/loginsApi';
import type { LoginStatus } from '../types';

const LOGINS_QUERY_KEY = ['logins'];

/**
 * Hook to fetch the current user's login statuses.
 *
 * Polls every 60s so that out-of-band changes (broker refresh, admin
 * disabling a provider) surface without a manual page reload.
 */
export function useLogins() {
  return useQuery<LoginStatus[]>({
    queryKey: LOGINS_QUERY_KEY,
    queryFn: loginsApi.list,
    refetchInterval: 60_000,
  });
}

/**
 * Hook to begin the direct-OIDC login flow for a provider.
 */
export function useStartLogin() {
  return useMutation({
    mutationFn: ({ name, redirectUri }: { name: string; redirectUri?: string }) =>
      loginsApi.start(name, redirectUri),
  });
}

/**
 * Hook to complete the direct-OIDC login flow after redirect back from the IdP.
 */
export function useCompleteLoginCallback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      provider,
      code,
      codeVerifier,
      redirectUri,
    }: {
      provider: string;
      code: string;
      codeVerifier: string;
      redirectUri?: string;
    }) => loginsApi.completeCallback(provider, code, codeVerifier, redirectUri),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOGINS_QUERY_KEY });
    },
  });
}

/**
 * Hook to store a pasted secret for a static-credential provider.
 */
export function useSetStaticSecret() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, secret }: { name: string; secret: string }) =>
      loginsApi.setStaticSecret(name, secret),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOGINS_QUERY_KEY });
    },
  });
}

/**
 * Hook to disconnect a login for the current user.
 */
export function useDisconnectLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => loginsApi.disconnect(name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOGINS_QUERY_KEY });
    },
  });
}
